import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '@/lib/api';

export type UserRole = 'admin' | 'warehouse' | 'customer' | 'supplier';

export interface User {
  id: string;
  username: string;
  role: UserRole;
  warehouseCode?: string; // Only for warehouse users
  warehouseName?: string; // Only for warehouse users
  customerCode?: string; // Only for customer users
  customerName?: string; // Only for customer users
  supplierCode?: string; // Only for supplier users
  supplierName?: string; // Only for supplier users
  token?: string; // JWT token for API requests
}

interface UserContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAdmin: boolean;
  canAccessWarehouse: (warehouseCode: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('shiphub-user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('shiphub-user', JSON.stringify(user));
    } else {
      localStorage.removeItem('shiphub-user');
    }
  }, [user]);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Call API login endpoint
      const response = await authAPI.login(username, password);

      if (response.success && response.data) {
        const { token, user: apiUser } = response.data;

        // Create user object with token
        const user: User = {
          id: String(apiUser.id),
          username: apiUser.username,
          role: apiUser.role,
          warehouseCode: apiUser.warehouse_code,
          warehouseName: apiUser.warehouse_name,
          customerCode: apiUser.customer_code,
          customerName: apiUser.customer_name,
          supplierCode: apiUser.supplier_code,
          supplierName: apiUser.supplier_name,
          token, // Store token for API requests
        };

        setUser(user);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
  };



  const isAdmin = user?.role === 'admin';

  const canAccessWarehouse = (warehouseCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'warehouse' && user.warehouseCode === warehouseCode) return true;
    return false;
  };

  return (
    <UserContext.Provider value={{ user, login, logout, isAdmin, canAccessWarehouse }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}
